import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Input, message, Modal, Tooltip } from 'antd';
import { SearchOutlined, ShopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
// import { useNavigate } from 'react-router-dom';
import { getLogisticsProviders } from '../../../services/logistics';
import type { LogisticsProvider } from '../../../services/logistics';

interface TableParams {
  pagination?: TablePaginationConfig;
  searchText?: string;
}

const LogisticsProviderList: React.FC = () => {
  // const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LogisticsProvider[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取物流服务商列表
  const fetchData = async (params: TableParams = {}) => {
    setLoading(true);
    try {
      const { pagination: { current = 1, pageSize = 10 } = {} } = params;
      const response = await getLogisticsProviders({
        page: current,
        page_size: pageSize,
        search: params.searchText,
      });
      
      setData(response.data.results);
      setPagination({
        ...pagination,
        total: response.data.count,
      });
    } catch (error) {
      message.error('获取物流服务商列表失败');
      console.error('获取物流服务商列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ pagination });
  }, []);

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    fetchData({ pagination: newPagination, searchText });
  };

  const handleSearch = () => {
    fetchData({ pagination: { ...pagination, current: 1 }, searchText });
  };

  const columns: ColumnsType<LogisticsProvider> = [
    {
      title: '承运商代码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: '承运商名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: LogisticsProvider) => (
        <Space>
          <div
            style={{
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            {record.logo ? (
              <img src={record.logo} alt={text} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ShopOutlined style={{ color: '#bfbfbf', fontSize: 22 }} />
            )}
          </div>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '官网',
      dataIndex: 'website',
      key: 'website',
      width: 150,
      render: (text: string) => text ? (
        <a href={text} target="_blank" rel="noopener noreferrer">
          访问官网
        </a>
      ) : '-',
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      ellipsis: true,
      render: (text?: string) => {
        if (!text) return '-';
        return (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleViewInstructions(record)}>
            查看仓库发货说明
          </Button>
        </Space>
      ),
    },
  ];

  const handleViewInstructions = (record: LogisticsProvider) => {
    Modal.info({
      title: '仓库发货说明',
      content: (
        <div>
          <p>服务商：{record.name}（{record.code}）</p>
          <p>此处仅为UI占位，详细发货说明功能将后续完善。</p>
        </div>
      ),
      okText: '知道了'
    });
  };

  // 已移除“申请添加新服务商”按钮

  return (
    <div className="p-6">
      <Card title="物流承运商列表">
        <div className="mb-4 flex justify-between items-center">
          <Space>
            <Input
              placeholder="搜索服务商名称/代码"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
            />
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
          </Space>
        </div>

        <Table<LogisticsProvider>
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default LogisticsProviderList; 